import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  cancelClientSync,
  fetchClientSyncStatus,
  triggerClientSync,
  type ClientSyncProgress,
} from '../api/clientes';
import { useClientSyncProgress } from '../hooks/useClientSyncProgress';
import { queryKeys } from '../lib/pwa/queryConfig';
import { getAtuacao } from '../lib/delegacao/atuacao';

interface ClientSyncContextValue {
  syncState: ClientSyncProgress | null;
  startSync: () => Promise<void>;
  cancelSync: () => Promise<void>;
  dismissStatus: () => void;
  checkForActiveJob: () => Promise<void>;
}

const ClientSyncContext = createContext<ClientSyncContextValue | null>(null);

export function ClientSyncProvider({ children }: { children: ReactNode }) {
  const [sseEnabled, setSseEnabled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryClient = useQueryClient();

  const progress = useClientSyncProgress(sseEnabled);

  // Auto-dismiss logic
  useEffect(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }

    if (!progress) return;

    let delay: number | null = null;
    if (progress.status === 'COMPLETED') {
      const hasErrors = (progress.processos_com_erro ?? 0) > 0;
      delay = hasErrors ? 30_000 : 10_000;
    } else if (progress.status === 'CANCELLED') {
      delay = 10_000;
    }

    if (delay !== null) {
      dismissTimerRef.current = setTimeout(() => {
        setDismissed(true);
        setSseEnabled(false);
      }, delay);
    }

    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, [progress]);

  // Invalidate queries on completion
  useEffect(() => {
    if (progress?.status === 'COMPLETED') {
      queryClient.invalidateQueries({ queryKey: queryKeys.clientes.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.clientes.counts });
    }
  }, [progress?.status, queryClient]);

  const startSync = useCallback(async () => {
    setDismissed(false);
    await triggerClientSync();
    setSseEnabled(true);
  }, []);

  const cancelSync = useCallback(async () => {
    await cancelClientSync();
  }, []);

  const dismissStatus = useCallback(() => {
    setDismissed(true);
    setSseEnabled(false);
  }, []);

  // Lazy check — chamado quando a página de clientes monta (não no mount do provider)
  const checkForActiveJob = useCallback(async () => {
    // Importar clientes dos processos é do titular: na conta dele não há o que acompanhar
    // (rota não delegável — a consulta só renderia um 403 na tela do delegado).
    if (getAtuacao()) return;
    try {
      const status = await fetchClientSyncStatus();
      if (status.status === 'RUNNING' || status.status === 'PENDING') {
        setSseEnabled(true);
        setDismissed(false);
      }
    } catch {
      // Ignora erro — servidor pode estar indisponível
    }
  }, []);

  const syncState = dismissed ? null : progress;

  return (
    <ClientSyncContext.Provider value={{ syncState, startSync, cancelSync, dismissStatus, checkForActiveJob }}>
      {children}
    </ClientSyncContext.Provider>
  );
}

export function useClientSync() {
  const ctx = useContext(ClientSyncContext);
  if (!ctx) {
    throw new Error('useClientSync deve ser usado dentro de ClientSyncProvider');
  }
  return ctx;
}
